/* eslint-disable @typescript-eslint/naming-convention */
/* eslint-disable @typescript-eslint/no-misused-promises */
import React, { useState } from 'react'
import Box from '@mui/material/Box'
import { FormProvider, useForm, type SubmitHandler } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { useTheme } from '@mui/material/styles'
import { useParams } from 'react-router-dom'
import * as yup from 'yup'
import { useAppDispatch } from '../../../../store'
import { getSlotSectionApi } from '../../../../store/slices/distributionPlanSlice/apis'
import GenericDateTimePicker from '../../../../components/GenericDateTimePicker'
import { formatDateWithTime } from '../../../../helpers/formatDateWithTime'
import FormInput from '../../../../components/FormElements/FormInput'
import { Alert, AlertTitle } from '@mui/material'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import { isAfter } from 'date-fns'
import { SlotStatus, type ISlot } from '../../../../models/DistributionPlan'
import { type ISection } from '../../../../models/TermsSection'
import DefaultModalActions from '../../../../components/GenericModal/DefaultModalActions'
import { red } from '../../../../colors'
import api from '../../../../utils/api'
import { ApiAutoComplete } from '../../../../components/FormElements/ApiAutoComplete'
import MultiSelectApiAutoComplete from '../../../../components/FormElements/MultiSelectApiAutoComplete'
import { type IUser } from '../../../../models/User'
import { type ApiError } from '../../../../models/Common'
import ValidationAlert from '../../../../components/ValidationAlert'
import { notification } from '../../../../components/Notifications'
import { parseReleaseTimestamp, ReleaseMode } from '../../../../helpers/parseReleaseTimestamp'

interface FormSubmissionPayload {
  sectionId: string
  startTimestamp: string
  endTimestamp: string
  earlyAccessReleaseTimestamp_1?: string
  earlyAccessReleaseTimestamp_2?: string
}

interface CreateAndEditCoursesProps {
  selectedRow?: ISection
  slot: ISlot
  handleCloseModal?: () => void
}

export const CreateAndEditCourses: React.FC<CreateAndEditCoursesProps> = ({
  selectedRow,
  slot,
  handleCloseModal = () => { }
}) => {
  const [loading, setLoading] = useState<boolean>(false)
  const [errors, setErrors] = useState<string>()
  const dispatch = useAppDispatch()
  const theme = useTheme()
  const isEditAction = !!selectedRow
  const { linkedSection, studentCount, instructors, endTimestamp, startTimestamp, earlyAccessReleaseTimestamp_1, earlyAccessReleaseTimestamp_2 } = selectedRow ?? {}
  const { abbreviation, sectionId, title } = linkedSection ?? {}

  const { id } = useParams()

  const coursesAndEnrollmentFormSchema = yup.object().shape({
    sectionId: yup.string().required('Section is required'),
    startTimestamp: yup.date()
      .required('Default Start Date is required')
      .when('slot', {
        is: () => slot?.slotNodeDetails?.slotStatus !== SlotStatus.Draft &&
          slot?.slotNodeDetails?.slotStatus !== SlotStatus.Live &&
          slot?.slotNodeDetails?.slotStatus !== SlotStatus.Published &&
          slot?.slotNodeDetails?.slotStatus !== SlotStatus.Closed &&
          isEditAction,
        then: schema => schema.min(new Date(Date.now() + 24 * 60 * 60 * 1000), 'Start date must be > 24 hrs from now')
      })
      .max(
        yup
          .date()
          .transform((value, originalValue) => parseReleaseTimestamp(originalValue))
          .cast(slot.releaseTimestamp),
        'Start date must be before Release Date'),

    endTimestamp: yup.date()
      .required('Default End Date is required')
      .min(yup.ref('startTimestamp'), 'End Date must be after Start Date')
      .max(yup
        .date()
        .transform((value, originalValue) => parseReleaseTimestamp(originalValue))
        .cast(slot.releaseTimestamp),
      'End Date must be on or before Release Date')
      .test('is-24-hours-after-start', 'End date must be at least 1 day after the Start Date', function (value) {
        const { startTimestamp } = this.parent
        return value && startTimestamp && new Date(value).getTime() >= new Date(startTimestamp).getTime() + 24 * 60 * 60 * 1000
      }),

    earlyAccessReleaseTimestamp1: yup.date()
      .nullable()
      .min(yup.ref('endTimestamp'), 'Admin  Release Date must be after end Date')
      .max(yup
        .date()
        .transform((value, originalValue) => parseReleaseTimestamp(originalValue))
        .cast(slot.releaseTimestamp), 'Admin  Release Date must be on or before Release Date')
      .notRequired(),

    earlyAccessReleaseTimestamp2: yup.date()
      .nullable()
      .min(yup.ref('endTimestamp'), 'Instructor Release Date must be after end Date')
      .max(yup
        .date()
        .transform((value, originalValue) => parseReleaseTimestamp(originalValue))
        .cast(slot.releaseTimestamp), 'Instructor  Release Date must be on or before Release Date')
      .notRequired()
  })

  const selectedInstructors = instructors?.map((user: { id: string, title: string }) => ({
    user_id: user.id,
    name: user.title
  }))
  const methods = useForm<any>({
    resolver: yupResolver(coursesAndEnrollmentFormSchema),
    defaultValues: {
      sectionId: sectionId ?? null,
      studentCount,
      title,
      abbreviation,
      startTimestamp: startTimestamp ?? slot.slotStartTimestamp,
      endTimestamp: endTimestamp ?? slot.slotEndTimestamp,
      instructors: selectedInstructors,
      earlyAccessReleaseTimestamp1: earlyAccessReleaseTimestamp_1,
      earlyAccessReleaseTimestamp2: earlyAccessReleaseTimestamp_2
    }
  })

  const { handleSubmit, control, watch, formState: { isDirty, isSubmitting } } = methods

  const onSubmit: SubmitHandler<any> = async (values) => {
    const payload: FormSubmissionPayload = {
      sectionId: values.sectionId,
      startTimestamp: formatDateWithTime(values?.startTimestamp),
      endTimestamp: formatDateWithTime(values.endTimestamp)
    }
    // TODO Need to add support on our parser so if we pass earlyAccessReleaseTimestamp1 its parsing for BE as early_access_release_timestamp1 it shoud parse like early_access_release_timestamp_1

    if (values.earlyAccessReleaseTimestamp1) {
      payload.earlyAccessReleaseTimestamp_1 = formatDateWithTime(values.earlyAccessReleaseTimestamp1)
    }

    if (values.earlyAccessReleaseTimestamp2) {
      payload.earlyAccessReleaseTimestamp_2 = formatDateWithTime(values.earlyAccessReleaseTimestamp2)
    }

    try {
      let response
      if (id) {
        setLoading(true)
        if (isEditAction) {
          response = await api.patch(`/api/slots/${id}/sections/${selectedRow?.linkedSection?.sectionId}`, payload)
          notification(`${response?.data?.linkedSection?.abbreviation} updated successfully to ${response.data?.linkedSection?.title}`, { variant: 'success', subTitle: response?.data?.linkedSection?.abbreviation })
        } else {
          response = await api.post(`/api/slots/${id}/sections/`, payload)
          notification(`${response?.data?.linkedSection?.abbreviation} created successfully to ${response.data?.linkedSection?.title}`, { variant: 'success', subTitle: response?.data?.linkedSection?.abbreviation })
        }
        await dispatch(getSlotSectionApi({ urlId: id, params: { pageSize: 15 } }))
        handleCloseModal()
      }
    } catch (error) {
      const customError = error as ApiError
      if (customError.message) {
        setErrors(customError.message || 'Unknown error')
      }
    } finally {
      setLoading(false)
    }
  }
  // Determine if the start time should be disabled
  // If the slot status is 'Draft', the start time is not disabled (false)
  // Otherwise, if it's an edit action and there's a start timestamp, check if the start date is within 10 minutes from now
  // If the start date is within 24 hours from now, disable the start time (true), otherwise enable it (false)
  const isStartTimeDisabled = (selectedRow?.slotStatus === SlotStatus.Draft) ? false : (isEditAction && startTimestamp ? !isAfter(new Date(startTimestamp), new Date(Date.now() + 10 * 60 * 1000)) : undefined)
  // Determine if the end time should be disabled
  // If the slot status is 'Draft', the end time is not disabled (false)
  // Otherwise, if it's an edit action and there's an end timestamp, check if the end date is within a minute from now
  // If the end date is within 24 hours from now, disable the end time (true), otherwise enable it (false)
  const isEndTimeDisabled = (selectedRow?.slotStatus === SlotStatus.Draft) ? false : (isEditAction && endTimestamp ? !isAfter(new Date(endTimestamp), new Date(Date.now() + 1 * 60 * 1000)) : undefined)
  return (
    <FormProvider {...methods}>
      <Box mb={6} mt={5}>
        {isEditAction && (
          <Alert icon={<WarningAmberIcon sx={{ color: red[900], fontSize: '24px' }} />} variant='outlined' severity='warning'>
            <AlertTitle color={red[900]} sx={{ fontWeight: 600, fontSize: '14px' }}>
              Course section details are managed in your Terms. To manage them go to the Terms page.
            </AlertTitle>
          </Alert>
        )}
      </Box>
      <ValidationAlert errors={errors} />
      <form onSubmit={handleSubmit(onSubmit)}>
        <Box display="flex" flexDirection="column" gap={theme.spacing(6)}>

          <ApiAutoComplete
            apiEndpoint={'/api/sections/summary/'}
            label="Section ID"
            labelField="abbreviation"
            placeholder='Select Section ID'
            name="sectionId"
            required
            readOnly={isEditAction}
            selectedItem={linkedSection}
            apiParams={{ termId: slot?.term?.termId, slotId: slot?.slotId }}
          />

          {isEditAction && (
            <>
              <FormInput name="title" label="Section Title" control={control} required disabled={isEditAction} />
              <MultiSelectApiAutoComplete
                name='instructors'
                apiEndpoint='/api/users/'
                sortField='name'
                getOptionLabel={(option: IUser) => option.name}
                getOptionValue={(option: IUser) => option.userId}
                getOptionSubLabel={(option: IUser) => option.email}
                apiExtraParams={{ isFaculty: true }}
                label='Instructor(s)'
                disabled
              />
              <FormInput name="studentCount" label="Enrollment" control={control} required disabled={isEditAction} />
            </>
          )}
          <GenericDateTimePicker name="startTimestamp" label="Start Date" required disabled={isStartTimeDisabled} minDate={new Date()} />
          <GenericDateTimePicker name="endTimestamp" label="End Date" required disabled={isEndTimeDisabled} minDate={watch('startTimestamp') || new Date()} />
          {slot.releaseTimestamp !== ReleaseMode.Rolling &&
            <>
              <GenericDateTimePicker name="earlyAccessReleaseTimestamp1" label="Instructor Release Date" minDate={watch('endTimestamp') || new Date()} />
              <GenericDateTimePicker name="earlyAccessReleaseTimestamp2" label="Admin Release Date" minDate={watch('endTimestamp') || new Date()} />
            </>}
        </Box>

        <DefaultModalActions
          destructiveButtonProps={{
            onClick: handleCloseModal
          }}
          confirmButtonProps={{
            text: isEditAction ? 'Save' : 'Create',
            loading,
            type: 'submit',
            disabled: isSubmitting || !isDirty
          }}
        />
      </form>
    </FormProvider>
  )
}
